import classNames from "classnames";
import React from "react";
import { Svg, SvgSize } from "../../../../Shared/Assets/Svg/React";
import { useTranslationContext } from "../../context";
import { ApiError, isConnectionError, isTimeoutError } from "../../utilities/apiClient";

interface ErrorMessageProps {
  error: Error | ApiError | null;
  onRetry?: () => void;
  onTimeslotConflict?: () => void;
  /** Shown for a 403 (the phone verification has expired), to verify the phone number again */
  onPhoneVerificationExpired?: () => Promise<void>;
  className?: string;
}

/**
 * Component for displaying error messages with optional retry functionality.
 *
 * Renders a styled error banner with an icon, message text, and optional action link.
 * Handles both generic Error and ApiError types with appropriate messaging.
 *
 * **Accessibility:**
 * - Uses role="alert" for immediate screen reader announcement
 * - Error details announced immediately
 * - Action links properly labeled
 *
 * @example
 * ```tsx
 * <ErrorMessage
 *   error={apiError}
 *   onRetry={() => refetchData()}
 * />
 * ```
 */
export function ErrorMessage({
  error,
  onRetry,
  onTimeslotConflict,
  onPhoneVerificationExpired,
  className = ""
}: ErrorMessageProps) {
  const { translations } = useTranslationContext();

  if (!error) {
    return null;
  }

  const isApiError = error instanceof ApiError;
  const isTimeslotConflict = isApiError && error.statusCode === 409;
  const isPhoneVerificationExpired =
    isApiError && error.statusCode === 403 && !!onPhoneVerificationExpired;

  const getErrorMessage = () => {
    // No response from the server: show readable text instead of the raw technical message
    if (isConnectionError(error)) {
      return isTimeoutError(error)
        ? translations?.errorTimeout ||
            "This is taking longer than expected. Please try again."
        : translations?.errorNetwork ||
            "We couldn't connect. Check your internet connection and try again.";
    }

    if (isPhoneVerificationExpired) {
      return (
        translations?.errorVerificationExpired ||
        "Your phone verification has expired. Please verify your phone number again."
      );
    }

    if (isApiError) {
      switch (error.statusCode) {
        case 400:
          return (
            translations?.errorBadRequest ||
            "Invalid request. Please check your information and try again."
          );
        case 401:
        case 403:
          return (
            translations?.errorForbidden ||
            "You don't have permission to perform this action."
          );
        case 404:
          return (
            translations?.errorNotFound || "The requested resource could not be found."
          );
        case 409:
          return (
            translations?.errorTimeslotTaken ||
            "Sorry, this time has just been booked by someone else. Please choose another time to continue."
          );
        case 500:
          return (
            translations?.errorServerError ||
            "A server error occurred. Please try again later."
          );
        case 503:
          return (
            translations?.errorServiceUnavailable ||
            "The service is temporarily unavailable. Please try again in a moment."
          );
        default:
          return error.message;
      }
    }

    return error.message || translations?.errorGeneric || "An unexpected error occurred.";
  };

  const getAction = (): {
    handler?: () => void | Promise<void>;
    label: string;
    name: string;
  } => {
    if (isTimeslotConflict) {
      return {
        handler: onTimeslotConflict,
        label: translations?.errorChooseATime || "Choose a time",
        name: "chooseAnotherTime"
      };
    }

    if (isPhoneVerificationExpired) {
      return {
        handler: onPhoneVerificationExpired,
        label: translations?.verifyPhoneAgain || "Verify phone number again",
        name: "verifyPhoneAgain"
      };
    }

    return {
      handler: onRetry,
      label: translations?.errorRetry || "Try again",
      name: "retry"
    };
  };

  const action = getAction();

  const handleActionClick = (e: React.MouseEvent) => {
    e.preventDefault();
    Promise.resolve(action.handler?.()).catch(err =>
      console.error(`Error message action "${action.name}" failed:`, err)
    );
  };

  return (
    <div
      role="alert"
      className={classNames(
        "flex gap-xs p-md text-t-pink bg-pink-400 border border-pink-600 rounded-xs",
        className
      )}
    >
      <div className="flex-shrink-0">
        <Svg
          href="/dist/sprite.svg#sys-info"
          size={SvgSize.MD}
          additionalClass="fill-current"
        />
      </div>
      <p className="text-t-sm">
        {getErrorMessage()}
        {action.handler && (
          <>
            {" "}
            <a
              href="#"
              onClick={handleActionClick}
              data-online-booking-action={action.name}
              className={classNames(
                "relative inline-block font-t-bold text-dark-blue transition-short-in-out",
                "underline decoration-current",
                "hover:text-dark-blue-400 focus-outline"
              )}
            >
              {action.label}
            </a>
          </>
        )}
      </p>
    </div>
  );
}

/**
 * Inline error message for form fields.
 *
 * Simple inline error display with role="alert" for accessibility.
 *
 * @example
 * ```tsx
 * <InlineError message="This field is required" />
 * ```
 */
export function InlineError({
  message,
  className = ""
}: {
  message: string;
  className?: string;
}) {
  return (
    <p className={classNames("mt-xs text-t-sm text-error-200", className)} role="alert">
      {message}
    </p>
  );
}
